import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AliveState } from '../../core/ActiveState';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { QuickPreviewService } from './quick-preview.service';
import { TooltipRef } from '../zalamo-tooltip/tooltip-ref';
import type { ZalamoTooltipPosition } from '../zalamo-tooltip/zalamo-tooltip.directive';
import { ZalamoTooltipService } from '../zalamo-tooltip/zalamo-tooltip.service';
import { Router } from '@angular/router';

@Directive({
    selector: '[libSmartPreview]',
    standalone: false
})
export class SmartPreviewDirective extends AliveState implements OnDestroy {
  @Input() libSmartPreview: string | TemplateRef<any>;
  shouldBeVisible$ = new BehaviorSubject(false);
  ref: TooltipRef<any> = null;
  @Output() smartPreviewVisited = new EventEmitter();
  @Output() smartPreviewOpen = new EventEmitter();
  @Input() position: ZalamoTooltipPosition = 'right';
  @Input() smartPreviewDisabled = false;

  @Input() data: any;

  constructor(
    private me: ElementRef,
    private previewService: QuickPreviewService,
    private _viewContainerRef: ViewContainerRef,
    private tooltipService: ZalamoTooltipService,
    private router: Router
  ) {
    super();
  }

  @HostListener(':pointerenter') pointerEntered() {
    if (this.smartPreviewDisabled) {
      return true;
    }
    this.shouldBeVisible$.next(true);
  }

  @HostListener(':pointerleave') pointerLeft() {
    if (this.smartPreviewDisabled) {
      return true;
    }
    this.shouldBeVisible$.next(false);
  }

  @HostListener(':click') clicked() {
    if (this.smartPreviewDisabled) {
      return true;
    }
    this.shouldBeVisible$.next(false);
  }

  ngOnInit() {
    this.subscribeWhileAlive(
      this.router.events.pipe(
        filter(() => !!this.ref),
        tap(() => {
          this.ref.close();
          this.smartPreviewOpen.emit(false);
          this.ref = null;
        })
      ),
      this.shouldBeVisible$.pipe(
        distinctUntilChanged(),
        debounceTime(750),
        tap(async (res) => {
          if (res) {
            if (!this.ref) {
              let willShow = null;
              console.log(this.libSmartPreview);
              if (typeof this.libSmartPreview === 'string') {
                willShow = await this.previewService.askForPreview(
                  this.libSmartPreview,
                  this.me.nativeElement,
                  this._viewContainerRef,
                  this.position,
                  { type: this.libSmartPreview, ...this.data }
                );
              } else {
                willShow = this.tooltipService.open(
                  this.libSmartPreview,
                  this.position,
                  this.me.nativeElement
                );
              }

              if (willShow) {
                this.smartPreviewOpen.emit(true);
                this.ref = willShow;
                willShow._containerInstance.entered
                  .pipe(takeUntil(willShow.afterClosed()))
                  .subscribe(() => {
                    this.shouldBeVisible$.next(true);
                    this.smartPreviewVisited.emit(true);
                  });
                willShow._containerInstance.left
                  .pipe(takeUntil(willShow.afterClosed()))
                  .subscribe(() => {
                    this.shouldBeVisible$.next(false);
                    this.smartPreviewVisited.emit(false);
                  });
                willShow
                  .afterClosed()
                  .pipe(first())
                  .subscribe(() => {
                    this.ref = null;
                    this.smartPreviewOpen.emit(false);
                  });
              }
            }
          } else {
            if (this.ref) {
              this.ref.close();
            }
          }
        })
      )
    );
  }

  ngOnDestroy() {
    if (this.ref) {
      this.ref.close();
      this.smartPreviewOpen.emit(false);
    }
  }
}
